import React, { useEffect, useState, useCallback } from 'react';
import { FaSearch, FaSync, FaCoins, FaFilter } from 'react-icons/fa';
import { imPointsApiService } from '../../../service/admin/imPointsApiService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import './style.css';

const TYPE_OPTIONS = [
  { label: 'All Types', value: '' },
  { label: 'Assigned', value: 'assigned' },
  { label: 'Used', value: 'used' },
  { label: 'Expired', value: 'expired' },
];

const PAYMENT_METHOD_OPTIONS = [
  { label: 'All Methods', value: '' },
  { label: 'Razorpay', value: 'razorpay' },
  { label: 'Wallet', value: 'wallet' },
  { label: 'IM Points Only', value: 'im_points' },
];

const paymentMethodBadge = method => {
  if (!method) return null;

  const map = {
    razorpay: { bg: '#ede9fe', color: '#6d28d9', label: 'Razorpay' },
    wallet: { bg: '#dbeafe', color: '#1d4ed8', label: 'Wallet' },
    im_points: { bg: '#dcfce7', color: '#166534', label: 'IM Only' },
  };

  const s = map[method] || { bg: '#f3f4f6', color: '#6b7280', label: method };

  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        borderRadius: 4,
        padding: '1px 6px',
        fontSize: 10,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
};

export default function IMPointsTransactionHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [limit, setLimit] = useState(20);
  const [stats, setStats] = useState({ totalAssigned: 0, totalUsed: 0, totalExpired: 0, totalRupeesDeducted: 0 });

  const message = useMessage();

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    if (search) params.set('search', search);
    if (typeFilter) params.set('type', typeFilter);
    if (paymentFilter) params.set('paymentMethod', paymentFilter);
    if (dateFrom) params.set('from', dateFrom);
    if (dateTo) params.set('to', dateTo);
    return `?${params.toString()}`;
  }, [page, limit, search, typeFilter, paymentFilter, dateFrom, dateTo]);

  const fetchTransactions = useCallback(() => {
    setLoading(true);
    imPointsApiService
      .getTransactions(buildQuery())
      .then(res => {
        if (res?.status?.code === 200) {
          setTransactions(res.data?.transactions || []);
          setPages({
            currentPage: res.data?.currentPage || 1,
            totalPages: res.data?.totalPages || 1,
            totalItems: res.data?.totalItems || 0,
          });
          if (res.data?.stats) {
            setStats(res.data.stats);
          }
        }
      })
      .catch(() => message.showToast('Failed to load transactions', ToastTypes.ERROR))
      .finally(() => setLoading(false));
  }, [buildQuery]);

  useEffect(() => {
    fetchTransactions();
  }, [page, limit, typeFilter, paymentFilter, dateFrom, dateTo]);

  // Only fetch when search has 3+ chars or is empty
  useEffect(() => {
    if (search.length >= 3 || search.length === 0) {
      fetchTransactions();
    }
  }, [search]);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearch(value);
    if (value.length >= 3 || value.length === 0) {
      setPage(1);
    }
  };

  const handleLimitChange = newLimit => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleTypeChange = e => {
    setTypeFilter(e.target.value);
    setPage(1);
  };

  const handleDateChange = (field, value) => {
    if (field === 'from') setDateFrom(value);
    else setDateTo(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('');
    setPaymentFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const typeBadgeClass = type => {
    if (type === 'assigned') return 'imp-badge-assigned';
    if (type === 'used') return 'imp-badge-used';
    if (type === 'expired') return 'imp-badge-expired';
    return '';
  };

  const renderPageButtons = () => {
    const btns = [];
    const { currentPage, totalPages } = pages;
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    if (start > 1) {
      btns.push(
        <button key={1} className="imp-page-btn" onClick={() => setPage(1)}>
          1
        </button>
      );
      if (start > 2)
        btns.push(
          <span key="s1" style={{ fontSize: 10, color: '#9ca3af' }}>
            …
          </span>
        );
    }

    for (let i = start; i <= end; i++) {
      btns.push(
        <button key={i} className={`imp-page-btn ${i === currentPage ? 'active' : ''}`} onClick={() => setPage(i)}>
          {i}
        </button>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1)
        btns.push(
          <span key="s2" style={{ fontSize: 10, color: '#9ca3af' }}>
            …
          </span>
        );
      btns.push(
        <button key={totalPages} className="imp-page-btn" onClick={() => setPage(totalPages)}>
          {totalPages}
        </button>
      );
    }

    return btns;
  };

  return (
    <div className="imp-page">
      {/* Header */}
      <div className="imp-header">
        <h4>
          <FaCoins style={{ marginRight: 6, color: '#4f46e5' }} />
          IM Points – Transaction History
        </h4>
        <button
          className="imp-btn imp-btn-outline"
          onClick={() => {
            setPage(1);
            fetchTransactions();
          }}
          title="Refresh"
        >
          <FaSync size={10} />
        </button>
      </div>

      {/* Stats row */}
      <div className="imp-stats-row">
        <div className="imp-stat-card">
          <div className="imp-stat-value" style={{ color: '#1d4ed8' }}>
            {(stats.totalAssigned || 0).toLocaleString()}
          </div>
          <div className="imp-stat-label">Total Assigned (pts)</div>
        </div>
        <div className="imp-stat-card">
          <div className="imp-stat-value" style={{ color: '#065f46' }}>
            {(stats.totalUsed || 0).toLocaleString()}
          </div>
          <div className="imp-stat-label">Total Used (pts)</div>
        </div>
        {/* <div className="imp-stat-card" style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}>
          <div className="imp-stat-value" style={{ color: "#dc2626" }}>
            ₹{(stats.totalRupeesDeducted || 0).toLocaleString()}
          </div>
          <div className="imp-stat-label" style={{ color: "#dc2626", fontWeight: 600 }}>
            Total ₹ Deducted (Orders)
          </div>
        </div> */}
        <div className="imp-stat-card">
          <div className="imp-stat-value" style={{ color: '#991b1b' }}>
            {(stats.totalExpired || 0).toLocaleString()}
          </div>
          <div className="imp-stat-label">Total Expired (pts)</div>
        </div>
        <div className="imp-stat-card">
          <div className="imp-stat-value">{pages.totalItems}</div>
          <div className="imp-stat-label">Total Records</div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="imp-filter-bar">
        <div className="imp-search-wrap">
          <FaSearch />
          <input className="imp-search-input" placeholder="Search by name, email…" value={search} onChange={handleSearchChange} />
        </div>
        <select className="imp-filter-select" value={typeFilter} onChange={handleTypeChange}>
          {TYPE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          className="imp-filter-select"
          value={paymentFilter}
          onChange={e => {
            setPaymentFilter(e.target.value);
            setPage(1);
          }}
        >
          {PAYMENT_METHOD_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input className="imp-date-input" type="date" value={dateFrom} onChange={e => handleDateChange('from', e.target.value)} title="From date" />
        <input className="imp-date-input" type="date" value={dateTo} onChange={e => handleDateChange('to', e.target.value)} title="To date" />
        <select
          className="imp-filter-select"
          value={limit}
          onChange={e => handleLimitChange(Number(e.target.value))}
          style={{ width: 'auto' }}
        >
          <option value={10}>10 rows</option>
          <option value={20}>20 rows</option>
          <option value={50}>50 rows</option>
          <option value={100}>100 rows</option>
        </select>
        {(search || typeFilter || paymentFilter || dateFrom || dateTo) && (
          <button className="imp-btn imp-btn-outline imp-btn-sm" onClick={clearFilters}>
            <FaFilter size={9} />
            Clear
          </button>
        )}
      </div>

      {/* Table */}

      <div className="imp-table-wrap">
        <table className="imp-table">
          <thead>
            <tr>
              <th>#</th>
              <th>User</th>
              <th>Email</th>
              <th>Type</th>
              <th>IM Points</th>
              {/* <th>Deducted (₹)</th> */}
              <th>Payment</th>
              <th>Order ID</th>
              <th>Grand Total</th>
              {/* <th>Paid After IM</th> */}
              <th>Balance After</th>
              <th>Admin / %</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="imp-loading-row">
                  {Array.from({ length: 11 }).map((__, j) => (
                    <td key={j}>
                      <span className="imp-skeleton" />
                    </td>
                  ))}
                </tr>
              ))
            ) : transactions.length === 0 ? (
              <tr className="imp-no-data">
                <td colSpan={11}>
                  No transactions found.
                  {!typeFilter && <span style={{ color: '#6b7280', marginLeft: 6 }}>"Used" entries appear here after a user completes an order with IM Points discount.</span>}
                </td>
              </tr>
            ) : (
              transactions.map((tx, idx) => {
                const snap = tx.orderSnapshot;
                const isUsed = tx.type === 'used';
                const isExpired = tx.type === 'expired';
                const orderNo = snap?.upsaleoOrderId || (tx.orderId ? tx.orderId.toString().slice(-8) : null);

                return (
                  <tr key={tx._id || idx} style={isUsed ? { background: '#fff7ed' } : undefined}>
                    <td style={{ color: '#9ca3af' }}>{(pages.currentPage - 1) * limit + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>
                      {tx.user?.firstName} {tx.user?.lastName}
                    </td>
                    <td style={{ color: '#4f46e5' }}>{tx.user?.email || '—'}</td>
                    <td>
                      <span className={`imp-badge ${typeBadgeClass(tx.type)}`}>{tx.type || '—'}</span>
                    </td>

                    {/* IM Points used / assigned */}
                    <td
                      style={{
                        fontWeight: 700,
                        color: isUsed || isExpired ? '#ef4444' : '#059669',
                      }}
                    >
                      {isUsed || isExpired ? '−' : '+'}
                      {tx.points != null ? tx.points.toLocaleString() : '—'}
                    </td>
                    {/* Deducted rupee amount — only meaningful for "used" */}
                    {/* <td style={{ fontWeight: 700, color: isUsed ? "#dc2626" : "#9ca3af" }}>
                      {isUsed && snap?.imDiscountApplied != null
                        ? `₹${snap.imDiscountApplied.toLocaleString()}`
                        : isUsed && tx.points != null
                          ? `₹${tx.points.toLocaleString()}`
                          : "—"}
                    </td> */}
                    {/* Payment method */}
                    <td>{isUsed ? paymentMethodBadge(tx.paymentMethod) || <span style={{ color: '#9ca3af' }}>—</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    {/* Order number */}
                    <td style={{ color: isUsed ? '#1d4ed8' : '#9ca3af', fontSize: 11, fontWeight: isUsed ? 600 : 400 }}>{orderNo || '—'}</td>
                    {/* Grand Total incl. GST */}
                    <td style={{ color: '#374151', fontSize: 11 }}>{snap?.grandTotalInclGst != null ? `₹${snap.grandTotalInclGst.toLocaleString()}` : '—'}</td>
                    {/* Paid after IM discount */}
                    {/* <td style={{ color: "#059669", fontSize: 11, fontWeight: 600 }}>
                      {snap?.payableAfterIm != null
                        ? `₹${snap.payableAfterIm.toLocaleString()}`
                        : "—"}
                    </td> */}
                    {/* Balance remaining */}
                    <td>{tx.remainingAfter != null ? tx.remainingAfter.toLocaleString() : '—'}</td>
                    <td style={{ color: '#6b7280', fontSize: 10 }}>
                      {tx.adminEmail || '—'}
                      {snap?.percentageApplied != null && snap.percentageApplied > 0 && (
                        <span
                          style={{
                            marginLeft: 4,
                            background: '#ede9fe',
                            color: '#6d28d9',
                            borderRadius: 3,
                            padding: '1px 4px',
                            fontSize: 9,
                            fontWeight: 600,
                          }}
                        >
                          {snap.percentageApplied}%
                        </span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', color: '#6b7280' }}>{tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages.totalPages > 1 && (
        <div className="imp-pagination">
          <span>
            Page {pages.currentPage} of {pages.totalPages} &nbsp;·&nbsp; {pages.totalItems} records
          </span>
          <div className="imp-pagination-btns">
            <button className="imp-page-btn" disabled={pages.currentPage <= 1} onClick={() => setPage(p => p - 1)}>
              ‹
            </button>
            {renderPageButtons()}
            <button className="imp-page-btn" disabled={pages.currentPage >= pages.totalPages} onClick={() => setPage(p => p + 1)}>
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
